import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useState } from 'react'

import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { deleteEntity, getEntities } from '@/services/entities.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

import AddEditAccountSlideout from './AddEditAccountSlideout'

const Accounts = observer(() => {
  // Context
  const { isXverseAdmin } = useContext(UserStoreContext)

  // State
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [showAccountSlideout, setShowAccountSlideout] = useState(false)
  const [editAccount, setEditAccount] = useState(null)

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { data: entities, isPending } = useQuery({
    queryKey: ['entities', sorting, pagination],
    queryFn: () => getEntities(sorting, pagination),
    placeholderData: keepPreviousData,
  })
  const { isPending: pendingDelete, mutate: deleteAccount } = useMutation({
    mutationFn: (data) => deleteEntity(data.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Account deleted successfully')

      // Refresh Entities lists for the EntitySelector and Accounts page
      queryClient.invalidateQueries({ queryKey: ['all-entities'] })
      queryClient.invalidateQueries({ queryKey: ['entities'] })
    },
  })

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button disabled={!isXverseAdmin} onClick={() => setShowAccountSlideout(true)}>
          Add Account
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className="text-action cursor-pointer text-left hover:underline"
                disabled={!isXverseAdmin}
                onClick={() => {
                  setEditAccount(row.original)
                  setShowAccountSlideout(true)
                }}
              >
                {row.original.name}
              </button>
            ),
          },
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditAccount(row.original)
                  setShowAccountSlideout(true)
                }}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
            ),
            size: 80,
          },
          {
            id: 'delete',
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Are you sure you want to delete the account: ${row.original.name}?`}
                confirmTitle="Delete Account"
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="destructive"
                size="sm"
                loading={pendingDelete}
                onClick={() => deleteAccount(row.original)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Delete account</span>
              </ConfirmButton>
            ),
            size: 80,
          },
        ]}
        data={entities?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={entities?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />

      {showAccountSlideout && (
        <AddEditAccountSlideout
          editAccount={editAccount}
          resetAndClose={() => {
            setShowAccountSlideout(false)
            setEditAccount(null)
          }}
        />
      )}
    </>
  )
})

export default Accounts
